import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { memo } from 'react';
import { useSelector } from 'react-redux';
import Carousel from '@comp/Carousel/Carousel';
import useEnroll from '@hooks/useEnroll';
import Dialog from '@comp/Dialog/Dialog';
import { Spacings, mediaQuery } from '@styles/index';
import { staticStrings } from '@constants/index';
import { getRecommendedCourses } from '@selectors/syllabus';
import { useIsMobile } from '@stores/AppCommonData';
import PlusCard from './PlusCard';
import { enrollProps } from './syllabusHelper';
import BlockWrapper from './BlockWrapper';

const StyledPlusCard = styled(PlusCard)`
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_10B} ${Spacings.SPACING_8B}
    ${Spacings.SPACING_8B};
  > [orientation='horizontal'] {
    padding-top: ${Spacings.SPACING_0B};
    padding-bottom: ${Spacings.SPACING_0B};
  }
  ${mediaQuery.uptoMobile} {
    padding: 0;
  }
`;

const RecommendedCoursesWrapper = styled(BlockWrapper)`
  margin-bottom: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    grid-template-columns: repeat(10, 1fr);
    grid-column: span 10;
    grid-gap: 0;
    grid-column-gap: ${Spacings.SPACING_2B};
    margin-bottom: ${Spacings.SPACING_14B};
    display: block;
  }
`;

const RecommendedCoursesContent = ({ toggleEnroll, courses }) => {
  const isMobile = useIsMobile();
  const numberOfItems = isMobile ? courses.length : 1;
  return (
    <Carousel
      label="Courses for you"
      numberOfItems={numberOfItems}
      paddingBottom="0"
      showNavButtons
    >
      {courses.map((courseData, index) => (
        <StyledPlusCard
          key={courseData.course}
          courseData={courseData}
          toggleEnroll={toggleEnroll}
          index={index}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          gridColumn={isMobile ? 'span 6' : 'span 18'}
        />
      ))}
    </Carousel>
  );
};

RecommendedCoursesContent.propTypes = {
  toggleEnroll: PropTypes.func.isRequired,
  courses: PropTypes.arrayOf(PropTypes.object).isRequired
};

const MemoizedRecommendedCoursesContent = memo(RecommendedCoursesContent);

const RecommendedCourses = ({ topic }) => {
  const courses = useSelector((state) =>
    getRecommendedCourses(state, topic.uid)
  );
  const { isUnEnrollModalOpen, toggleEnroll, acceptOnClick, onCloseModal } =
    useEnroll(enrollProps);

  if (!courses.length) return null;
  return (
    <RecommendedCoursesWrapper>
      <MemoizedRecommendedCoursesContent
        toggleEnroll={toggleEnroll}
        courses={courses}
        topic={topic}
      />
      <Dialog
        onClose={onCloseModal}
        dialogState={isUnEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message={staticStrings.UNENROLL_MESSAGE}
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
    </RecommendedCoursesWrapper>
  );
};

RecommendedCourses.propTypes = {
  topic: PropTypes.objectOf(PropTypes.string).isRequired
};

export default RecommendedCourses;
